import { omitPathParameters, replacePathParameters } from "../../utils.ts";
import {
  type OperationResponse,
  type Parameters,
  type UnknownParamsObject,
} from "../../common.ts";
import type { AxiosInstance, AxiosRequestConfig } from "axios";

import type * as Paths from "./paths";

interface OperationMethods {
  "getList"(
    parameters?: Parameters<Paths.GetList.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetList.Responses.$200>;

  "createRecord"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.CreateRecord.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.CreateRecord.Responses.$200>;

  "getRecord"(
    parameters?: Parameters<Paths.GetRecord.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetRecord.Responses.$200>;

  "getProducts"(
    parameters?: Parameters<
      Paths.GetProducts.PathParameters & Paths.GetProducts.QueryParameters
    >,
    data?: null,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetProducts.Responses.$200>;

  "manageProducts"(
    parameters: Parameters<
      Paths.ManageProducts.QueryParameters & Paths.ManageProducts.PathParameters
    >,
    data: Paths.ManageProducts.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.ManageProducts.Responses.$200>;

  "updateRecord"(
    parameters?: Parameters<Paths.UpdateRecord.PathParameters> | null,
    data?: Paths.UpdateRecord.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.UpdateRecord.Responses.$200>;

  "deleteRecord"(
    parameters?: Parameters<Paths.DeleteRecord.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.DeleteRecord.Responses.$204>;

  "syncProducts"(
    parameters?: Parameters<Paths.SyncProducts.PathParameters> | null,
    data?: Paths.SyncProducts.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.SyncProducts.Responses.$200>;

  "getNewForm"(
    parameters?: Parameters<
      Paths.GetNewForm.QueryParameters & Paths.GetNewForm.PathParameters
    > | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetNewForm.Responses.$200>;
}

export const getManufacturerOperations = (client: AxiosInstance) => {
  const getList: OperationMethods["getList"] = (parameters, data, config) => {
    const url = "/manufacturer";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };

  const createRecord: OperationMethods["createRecord"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/manufacturer";
    return client.post(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  const getRecord: OperationMethods["getRecord"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/manufacturer/{id}";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };

  const updateRecord: OperationMethods["updateRecord"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/manufacturer/{id}";
    return client.put(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  const deleteRecord: OperationMethods["deleteRecord"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/manufacturer/{id}";
    return client.delete(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };

  const getNewForm: OperationMethods["getNewForm"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/manufacturer/new-form";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };

  const syncProducts: OperationMethods["syncProducts"] = // todo remove?
    (parameters, data, config) => {
      const url = "/manufacturer/sync-products/{id}";
      return client.put(replacePathParameters(url, parameters), data, {
        params: omitPathParameters(parameters, url),
        ...config,
      });
    };

  const getProducts: OperationMethods["getProducts"] = (
    parameters,
    _,
    config,
  ) => {
    const url = "/manufacturer/{id}/products";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  const manageProducts: OperationMethods["manageProducts"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/manufacturer/{id}/products/manage";
    return client.patch(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  return {
    getList,
    createRecord,
    getRecord,
    getProducts,
    updateRecord,
    deleteRecord,
    syncProducts,
    manageProducts,
    getNewForm,
  };
};
